import React, { createContext, useContext, useState, useCallback, useEffect, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import Toast from '@/shared/components/Toast';

import { ReactNode } from "react";

interface ToastMessage {
  id: string;
  message: ReactNode;
  type: 'success' | 'error' | 'info';
  details: string[];
  duration: number;
}

interface ToastContextType {
  showToast: (message: ReactNode, type?: 'success' | 'error' | 'info', details?: string[], duration?: number, toastId?: string) => void;
  dismissToast?: (id: string) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

/**
 * Extracts plain text from a ReactNode so we can write a clean string into
 * the ARIA live-region.  NVDA / JAWS reliably announce a simple text node;
 * they often silently skip announcements when complex HTML is injected.
 */
function toPlainText(node: ReactNode): string {
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(toPlainText).join(' ');
  if (React.isValidElement(node)) {
    const el = node as React.ReactElement<{ children?: ReactNode }>;
    return toPlainText(el.props.children);
  }
  return '';
}

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);
  const [domReady, setDomReady] = useState(false);
  const toastCounterRef = useRef(0);

  useEffect(() => {
    // Ensure #toast-root (visual container) exists.
    // In normal usage index.html pre-seeds all three elements; this block is
    // only a safety net for environments where index.html is not used.
    if (!document.getElementById('toast-root')) {
      const root = document.createElement('div');
      root.id = 'toast-root';
      root.className = 'toast-visual-container';
      root.setAttribute('aria-hidden', 'true');
      document.body.appendChild(root);
    }

    // Ensure the ARIA live-region divs exist as siblings of #toast-root.
    // They MUST exist before the first toast fires; screen readers only honour
    // live regions that were present in the DOM before content is injected.
    if (!document.getElementById('toast-live-polite')) {
      const polite = document.createElement('div');
      polite.id = 'toast-live-polite';
      polite.className = 'a11y-live-region';
      polite.setAttribute('role', 'status');
      polite.setAttribute('aria-live', 'polite');
      polite.setAttribute('aria-atomic', 'true');
      document.body.appendChild(polite);
    }

    if (!document.getElementById('toast-live-assertive')) {
      const assertive = document.createElement('div');
      assertive.id = 'toast-live-assertive';
      assertive.className = 'a11y-live-region';
      assertive.setAttribute('role', 'alert');
      assertive.setAttribute('aria-live', 'assertive');
      assertive.setAttribute('aria-atomic', 'true');
      document.body.appendChild(assertive);
    }

    setDomReady(true);
  }, []);

  /**
   * Write plain-text announcements into the ARIA live regions.
   *
   * Strategy:
   *  1. Only update when there are NEW toasts (non-empty list). When the list
   *     empties (toast dismissed), we do NOT clear the live region — clearing
   *     after announcement would cause NVDA to read an empty string.
   *  2. Clear the region first, then after a 50ms task-queue gap set the text.
   *     The gap forces NVDA/JAWS to observe two separate mutations and fire the
   *     announcement even when the text is identical to the last toast.
   *  3. Do NOT return a cleanup that cancels the timeout — if the effect
   *     re-runs (e.g. toast dismissed before 50ms) we still want the write to
   *     complete for the previous toast that was just added.
   */
  useEffect(() => {
    if (!domReady || toasts.length === 0) return;

    const politeToasts = toasts.filter((t) => t.type !== 'error');
    const assertiveToasts = toasts.filter((t) => t.type === 'error');

    if (politeToasts.length > 0) {
      const politeEl = document.getElementById('toast-live-polite');
      if (politeEl) {
        const text = politeToasts.map((t) => {
          const parts = [toPlainText(t.message), ...t.details].filter(Boolean);
          return parts.join('. ');
        }).join('. ');
        politeEl.textContent = '';
        setTimeout(() => { politeEl.textContent = text; }, 50);
      }
    }

    if (assertiveToasts.length > 0) {
      const assertiveEl = document.getElementById('toast-live-assertive');
      if (assertiveEl) {
        const text = assertiveToasts.map((t) => {
          const parts = [toPlainText(t.message), ...t.details].filter(Boolean);
          return parts.join('. ');
        }).join('. ');
        assertiveEl.textContent = '';
        setTimeout(() => { assertiveEl.textContent = text; }, 50);
      }
    }
  }, [toasts, domReady]);

  const showToast = useCallback((message: ReactNode, type: 'success' | 'error' | 'info' = 'success', details: string[] = [], duration: number = 3000, toastId?: string) => {
    toastCounterRef.current += 1;
    const id = toastId ?? `${Date.now()}-${toastCounterRef.current}`;
    setToasts((prev) => {
      // If a toast with the same id already exists, replace it (deduplication)
      const exists = toastId ? prev.some((t) => t.id === id) : false;
      if (exists) {
        return prev.map((t) => t.id === id ? { id, message, type, details, duration } : t);
      }
      return [...prev, { id, message, type, details, duration }];
    });
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const dismissToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  // Visual toasts render into #toast-root (NOT the live-region divs).
  // The live-region divs receive only plain text (written imperatively above)
  // so that NVDA / JAWS reliably announce the message.
  const visualRoot = domReady ? document.getElementById('toast-root') : null;

  return (
    <ToastContext.Provider value={useMemo(() => ({ showToast, dismissToast }), [showToast, dismissToast])}>
      {children}
      {/* Visual toasts — rendered into #toast-root, NOT inside the live-region divs */}
      {visualRoot && ReactDOM.createPortal(
        <div className="toast-visual-container">
          {toasts.map((toast) => (
            <Toast
              key={toast.id}
              message={toast.message}
              type={toast.type}
              details={toast.details}
              duration={toast.duration}
              onClose={() => removeToast(toast.id)}
            />
          ))}
        </div>,
        visualRoot
      )}
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};
